/** @format */

import React, { useState, useEffect } from "react";
import {
  XMarkIcon,
  PlayIcon,
  AcademicCapIcon,
  EyeIcon,
  PencilIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";

const FreeResourceModal = ({ isOpen, onClose, onSave, video, mode }) => {
  const [formData, setFormData] = useState({
    title: "",
    youtubeUrl: "",
    comments: [],
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (video && mode !== "add") {
      setFormData({
        title: video.title || "",
        youtubeUrl: video.youtubeUrl || "",
        comments: video.comments || [],
      });
    } else {
      setFormData({
        title: "",
        youtubeUrl: "",
        comments: [],
      });
    }
    setErrors({});
  }, [video, mode]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.youtubeUrl.trim()) {
      newErrors.youtubeUrl = "YouTube URL is required";
    } else if (!isValidYouTubeUrl(formData.youtubeUrl)) {
      newErrors.youtubeUrl = "Please enter a valid YouTube URL";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidYouTubeUrl = (url) => {
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+/;
    return youtubeRegex.test(url);
  };

  const getYouTubeVideoId = (url) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const getThumbnailUrl = (url) => {
    const videoId = getYouTubeVideoId(url);
    return videoId
      ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
      : null;
  };

  const getEmbedUrl = (url) => {
    const videoId = getYouTubeVideoId(url);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const dataToSave = {
        ...formData,
        updatedAt: new Date().toISOString(),
      };

      if (mode === "add") {
        dataToSave.createdAt = new Date().toISOString();
      }

      await onSave(dataToSave);
    } catch (error) {
      console.error("Error saving video:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div
            className="absolute inset-0 bg-gray-500 opacity-75"
            onClick={onClose}
          ></div>
        </div>

        <span
          className="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
        >
          &#8203;
        </span>

        <div className="inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-2xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                {mode === "view" ? (
                  <EyeIcon className="h-6 w-6 text-white mr-3" />
                ) : mode === "edit" ? (
                  <PencilIcon className="h-6 w-6 text-white mr-3" />
                ) : (
                  <AcademicCapIcon className="h-6 w-6 text-white mr-3" />
                )}
                <h3 className="text-xl font-semibold text-white">
                  {mode === "add"
                    ? "Add New Video"
                    : mode === "edit"
                    ? "Edit Video"
                    : "View Video"}
                </h3>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            {video?.id && (
              <div className="mt-2">
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white bg-opacity-20 text-white">
                  ID: {video.id}
                </span>
              </div>
            )}
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="bg-white px-6 py-6">
            <div className="space-y-6 max-h-[60vh] overflow-y-auto pr-2">
              {/* Basic Information */}
              <div className="space-y-4">
                <h4 className="text-lg font-medium text-gray-900 flex items-center">
                  <PlayIcon className="h-5 w-5 mr-2 text-green-600" />
                  Video Information
                </h4>

                {/* Title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Video Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => handleInputChange("title", e.target.value)}
                    disabled={mode === "view"}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${
                      errors.title ? "border-red-300" : "border-gray-300"
                    } ${
                      mode === "view" ? "bg-gray-50 text-gray-500" : "bg-white"
                    }`}
                    placeholder="Enter video title"
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                      {errors.title}
                    </p>
                  )}
                </div>

                {/* YouTube URL */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    YouTube URL *
                  </label>
                  <input
                    type="url"
                    value={formData.youtubeUrl}
                    onChange={(e) =>
                      handleInputChange("youtubeUrl", e.target.value)
                    }
                    disabled={mode === "view"}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors ${
                      errors.youtubeUrl ? "border-red-300" : "border-gray-300"
                    } ${
                      mode === "view" ? "bg-gray-50 text-gray-500" : "bg-white"
                    }`}
                    placeholder="https://www.youtube.com/watch?v=..."
                  />
                  {errors.youtubeUrl && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                      {errors.youtubeUrl}
                    </p>
                  )}
                  {formData.youtubeUrl && !errors.youtubeUrl && (
                    <p className="mt-1 text-sm text-green-600">
                      ✓ Valid YouTube URL
                    </p>
                  )}
                </div>

                {/* Video Preview */}
                {formData.youtubeUrl && !errors.youtubeUrl && (
                  <div className="space-y-3">
                    <h5 className="text-sm font-medium text-gray-700">
                      Video Preview
                    </h5>
                    <div className="bg-gray-100 rounded-lg p-4">
                      {getThumbnailUrl(formData.youtubeUrl) ? (
                        <div className="space-y-3">
                          <img
                            src={getThumbnailUrl(formData.youtubeUrl)}
                            alt="Video thumbnail"
                            className="w-full h-48 object-cover rounded-lg"
                          />
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-gray-600">
                              {formData.title || "Untitled Video"}
                            </span>
                            <a
                              href={formData.youtubeUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-full hover:bg-red-100 transition-colors duration-200"
                            >
                              <PlayIcon className="h-3 w-3 mr-1" />
                              Watch on YouTube
                            </a>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          <PlayIcon className="h-12 w-12 mx-auto mb-2" />
                          <p>Unable to load video preview</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Comments Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Comments
                  </label>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600">
                      Comments functionality will be available in future
                      updates.
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Current comments: {formData.comments?.length || 0}
                    </p>
                  </div>
                </div>
              </div>

              {/* Metadata */}
              {video && (
                <div className="space-y-4">
                  <h4 className="text-lg font-medium text-gray-900 flex items-center">
                    <AcademicCapIcon className="h-5 w-5 mr-2 text-green-600" />
                    Metadata
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">
                        Created:
                      </span>
                      <p className="text-gray-600">
                        {video.createdAt
                          ? new Date(video.createdAt).toLocaleString()
                          : "N/A"}
                      </p>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">
                        Updated:
                      </span>
                      <p className="text-gray-600">
                        {video.updatedAt
                          ? new Date(video.updatedAt).toLocaleString()
                          : "N/A"}
                      </p>
                    </div>
                    {video.createdBy && (
                      <div className="col-span-2">
                        <span className="font-medium text-gray-700">
                          Created By:
                        </span>
                        <p className="text-gray-600">{video.createdBy}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="mt-6 flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
              >
                Cancel
              </button>
              {mode !== "view" && (
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </div>
                  ) : mode === "add" ? (
                    "Add Video"
                  ) : (
                    "Update Video"
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default FreeResourceModal;
